import {
  getSubscriptionPublicRootPrefix,
  SUBSCRIPTION_CATALOG_CONFIG,
} from "../config/subscriptionCatalogConfig";
import { REQUIRED_SUBSCRIPTION_LIMIT_KEYS } from "../utils/subscriptionCatalogSchema";

let catalogCache = null;

const urlForPath = (path) => `${SUBSCRIPTION_CATALOG_CONFIG.cdnRoot}/${String(path).replace(/^\/+/, "")}`;

function withRefreshParam(url, force) {
  if (!force) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}catalogRefresh=${Date.now()}`;
}

async function fetchJson(url, label, { force = false } = {}) {
  const response = await fetch(withRefreshParam(url, force), {
    cache: force ? "no-store" : "default",
  });
  if (!response.ok) throw new Error(`${label} load failed (${response.status})`);
  return response.json();
}

async function checksumCatalog(catalog) {
  const json = JSON.stringify(catalog, null, 2);
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(json));
  return `sha256-${Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")}`;
}

export async function loadLatestSubscriptionCatalog({ force = false } = {}) {
  if (catalogCache && !force) return catalogCache;

  const rootManifest = await fetchJson(
    urlForPath(SUBSCRIPTION_CATALOG_CONFIG.rootManifestPath),
    "Subscription root manifest",
    { force }
  );
  if (!Array.isArray(rootManifest?.versions) || !rootManifest.versions.length) {
    throw new Error("Subscription manifest versions are missing");
  }
  const publicVersion = String(rootManifest.latestVersion || "");
  if (!/^v[1-9]\d*$/.test(publicVersion)) throw new Error("Invalid latest subscription version");
  if (!rootManifest.versions.includes(publicVersion)) throw new Error("Latest subscription version is not listed");
  const catalogPath = `${getSubscriptionPublicRootPrefix()}/${publicVersion}/catalog.json`;
  const catalog = await fetchJson(urlForPath(catalogPath), "Subscription catalog", { force });
  if (
    catalog?.app !== "MyHub"
    || catalog?.currency !== "INR"
    || !Array.isArray(catalog.plans)
    || !Array.isArray(catalog.billingCycles)
    || catalog.plans.some((plan) => (
      typeof plan?.trial?.enabled !== "boolean"
      || !Number.isInteger(plan?.trial?.days)
      || plan.trial.days < 0
      || (plan.trial.enabled && plan.trial.days === 0)
      || typeof plan?.trial?.oneTrialPerMerchant !== "boolean"
      || !Number.isFinite(Number(plan?.monthlyPrice))
      || Number(plan.monthlyPrice) < 0
      || (
        plan.billingCycles !== undefined &&
        (!Array.isArray(plan.billingCycles) || plan.billingCycles.some((cycleId) => (
          !catalog.billingCycles.some((cycle) => cycle.id === cycleId)
        )))
      )
      || REQUIRED_SUBSCRIPTION_LIMIT_KEYS.some((key) => (
        !Number.isFinite(Number(plan?.limits?.[key])) || Number(plan.limits[key]) < 0
      ))
    ))
  ) {
    throw new Error("Subscription catalog is invalid");
  }
  if ((await checksumCatalog(catalog)) !== rootManifest.checksum) {
    throw new Error("Subscription catalog checksum validation failed");
  }

  const value = { manifest: rootManifest, catalog, publicVersion };
  catalogCache = value;
  return value;
}

export function clearSubscriptionCatalogCache() {
  catalogCache = null;
}

export function resolveCatalogPlan(catalog, planId) {
  const normalizedPlanId = String(planId || "").trim().toLowerCase();
  if (!normalizedPlanId) return null;
  return (catalog?.plans || []).find((plan) => plan.id === normalizedPlanId) || null;
}

export function formatCatalogPlanName(planId, catalogPlan) {
  if (catalogPlan?.name) return catalogPlan.name;
  const normalizedPlanId = String(planId || "").trim();
  if (!normalizedPlanId) return "—";
  return normalizedPlanId.charAt(0).toUpperCase() + normalizedPlanId.slice(1);
}

export function getPlanBillingCycleIds(catalog, plan) {
  const catalogCycleIds = (catalog?.billingCycles || []).map((cycle) => cycle.id);
  if (!Array.isArray(plan?.billingCycles) || plan.billingCycles.length === 0) {
    return catalogCycleIds;
  }
  return plan.billingCycles.filter((cycleId) => catalogCycleIds.includes(cycleId));
}

export function planSupportsBillingCycle(catalog, plan, billingCycleId) {
  return getPlanBillingCycleIds(catalog, plan).includes(billingCycleId);
}

export function calculatePlanBillingAmount(plan, billingCycle) {
  const monthlyPrice = Number(plan?.monthlyPrice);
  const months = Number(billingCycle?.months);
  const discountPercent = Number(billingCycle?.discountPercent || 0);
  if (!Number.isFinite(monthlyPrice) || monthlyPrice < 0) return null;
  if (!Number.isFinite(months) || months <= 0) return null;
  if (!Number.isFinite(discountPercent) || discountPercent < 0 || discountPercent > 100) return null;
  return Math.round(monthlyPrice * months * (1 - discountPercent / 100));
}

export function resolveCatalogEntitlement(catalog, planId, requirement, role = "owner") {
  if (!requirement) return { allowed: true, plan: null, limit: null };
  if (requirement.roles && !requirement.roles.includes(role)) {
    return { allowed: false, reason: "ROLE_NOT_ALLOWED", plan: null, limit: null };
  }

  const normalizedPlanId = String(planId || "").trim().toLowerCase();
  const entitlementSource = (catalog?.plans || []).find((item) => item.id === normalizedPlanId);

  if (!entitlementSource) {
    return { allowed: false, reason: "PLAN_NOT_IN_CATALOG", plan: null, limit: null };
  }
  if (entitlementSource.modules?.[requirement.moduleId] !== true) {
    return { allowed: false, reason: "MODULE_DISABLED", plan: entitlementSource, limit: null };
  }

  const rawLimit = requirement.limitId
    ? entitlementSource.limits?.[requirement.limitId]
    : null;
  if (requirement.limitId) {
    const limit = String(rawLimit).trim().toLowerCase() === "unlimited"
      ? Number.POSITIVE_INFINITY
      : Number(rawLimit);
    if (!Number.isFinite(limit) || limit <= 0) {
      return { allowed: false, reason: "LIMIT_UNAVAILABLE", plan: entitlementSource, limit };
    }
    return { allowed: true, plan: entitlementSource, limit };
  }

  return { allowed: true, plan: entitlementSource, limit: null };
}

export function calculateGalleryImageLimit(plan) {
  const productItems = Number(plan?.limits?.productItems);
  const serviceItems = Number(plan?.limits?.serviceItems);
  const imagesPerItem = Number(plan?.limits?.imagesPerItem);
  if (!Number.isFinite(productItems) || productItems < 0) return null;
  if (!Number.isFinite(serviceItems) || serviceItems < 0) return null;
  if (!Number.isFinite(imagesPerItem) || imagesPerItem <= 0) return null;
  return (productItems + serviceItems) * imagesPerItem;
}
